import { useEffect, useState, useRef } from 'react'
import { motion, useInView } from 'framer-motion'

interface TypingAnimationProps {
  text: string
  speed?: number
  delay?: number
  className?: string
  cursorClassName?: string
  onComplete?: () => void
}

export default function TypingAnimation({
  text,
  speed = 50,
  delay = 0,
  className = '',
  cursorClassName = '',
  onComplete,
}: TypingAnimationProps) {
  const [displayText, setDisplayText] = useState('')
  const [showCursor, setShowCursor] = useState(true)
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true })
  const hasAnimated = useRef(false)

  useEffect(() => {
    if (!isInView || hasAnimated.current) return
    hasAnimated.current = true

    const timeout = setTimeout(() => {
      let i = 0
      const interval = setInterval(() => {
        if (i < text.length) {
          setDisplayText(text.slice(0, i + 1))
          i++
        } else {
          clearInterval(interval)
          onComplete?.()
          // Keep cursor blinking for a bit, then hide
          setTimeout(() => setShowCursor(false), 2000)
        }
      }, speed)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(timeout)
  }, [isInView, text, speed, delay, onComplete])

  return (
    <span ref={ref} className={className}>
      {displayText}
      {showCursor && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
          className={`inline-block w-[2px] h-[1em] ml-1 bg-electric-cyan align-middle ${cursorClassName}`}
        />
      )}
    </span>
  )
}

// Code typing animation with syntax highlighting simulation
export function CodeTypingAnimation({
  lines,
  speed = 30,
  lineDelay = 300,
  className = '',
}: {
  lines: Array<{ text: string; indent?: number; color?: string }>
  speed?: number
  lineDelay?: number
  className?: string
}) {
  const [currentLine, setCurrentLine] = useState(0)
  const [displayLines, setDisplayLines] = useState<string[]>([])
  const [currentText, setCurrentText] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true })
  const hasAnimated = useRef(false)

  useEffect(() => {
    if (!isInView || hasAnimated.current) return
    hasAnimated.current = true

    const typeLine = () => {
      if (currentLine >= lines.length) return

      const line = lines[currentLine]
      let i = 0

      const interval = setInterval(() => {
        if (i < line.text.length) {
          setCurrentText(line.text.slice(0, i + 1))
          i++
        } else {
          clearInterval(interval)
          setDisplayLines((prev) => [...prev, line.text])
          setCurrentText('')
          setCurrentLine((prev) => prev + 1)
        }
      }, speed)

      return () => clearInterval(interval)
    }

    const timeout = setTimeout(typeLine, lineDelay)
    return () => clearTimeout(timeout)
  }, [isInView, currentLine, lines, speed, lineDelay])

  return (
    <div ref={ref} className={`font-mono text-sm ${className}`}>
      {displayLines.map((line, i) => {
        const indent = lines[i]?.indent || 0
        const color = lines[i]?.color || 'text-white/80'
        return (
          <div key={i} className={color} style={{ paddingLeft: `${indent * 1.5}rem` }}>
            {line}
          </div>
        )
      })}
      {currentLine < lines.length && (
        <div
          className={lines[currentLine]?.color || 'text-white/80'}
          style={{ paddingLeft: `${(lines[currentLine]?.indent || 0) * 1.5}rem` }}
        >
          {currentText}
          <motion.span
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatType: 'reverse' }}
            className="inline-block w-2 h-4 ml-0.5 bg-electric-cyan"
          />
        </div>
      )}
    </div>
  )
}
